// Mirrored from mju-ai-workbench/shared/schemas/microsoft365.ts — keep the two identical but for this line and the import extensions.
/**
 * The Microsoft 365 integration (docs/plans/2026-10-microsoft-agent-365-and-defender.md,
 * Phase C; the contract with the AIOE is docs/plans/2026-10-agent-365-aioe-contract.md).
 *
 * Three things live here:
 *
 *  - the `microsoft365` clause of the workspace policy (`.mojoup/policy.json`,
 *    src/control/policyFile.ts): what content may ride on the telemetry a run
 *    produces, and whether this machine may send its runs to Agent 365 itself;
 *  - the audit severity every audit row carries (C2), so the AIOE's Sentinel
 *    connector never guesses;
 *  - the token request a host makes of the AIOE for the optional direct path
 *    (C1e), and the answer.
 *
 * The AIOE is the only party that holds a Microsoft credential. The Agent 365
 * blueprint is one multi-tenant app published from Mojo Up's tenant and
 * consented once per customer; nothing here or in the workbench names that
 * tenant, the app or any customer tenant — every id reaches a host in the
 * AIOE's token answer.
 *
 * No runtime dependency besides zod.
 */
import { z } from "zod";
/**
 * How much of a run's content (the prompt, the final message, tool
 * arguments) the telemetry carries:
 *
 *  - `none` — names, ids, counts, decisions; no text at all;
 *  - `redacted` (the default) — text after credential masking, the secret and
 *    personal-data patterns and the DLP engine; a text the DLP engine blocks
 *    is left out;
 *  - `full` — text with credentials masked and nothing else removed.
 *
 * A run whose session met a DLP block, or whose sensitivity floor says the
 * content never leaves the machine, carries no content whatever this says.
 */
export const telemetryContentSchema = z.enum(["none", "redacted", "full"]);
export const microsoft365PolicySchema = z.object({
    telemetryContent: telemetryContentSchema.default("redacted"),
    /**
     * How agent identities are cut (Phase A, built by the AIOE): one per
     * specialist per organisation (the default), or one per specialist per
     * project. Informational on the workbench: it reads the id it is given.
     */
    agentIdentity: z.object({ mode: z.enum(["specialist", "specialist-per-project"]).default("specialist") }).optional(),
    /**
     * The direct path (C1e): this machine sends its runs to Agent 365 itself,
     * with a short-lived token the AIOE mints for each agent identity, instead
     * of the AIOE forwarding them. Off unless the policy turns it on.
     */
    directExport: z
        .object({
        enabled: z.boolean().default(false),
        /** The `chat` spans of one run beyond this many are sampled out (a busy run would pass the 1 MB request limit). */
        maxChatSpansPerRun: z.number().int().min(1).max(10_000).default(200),
    })
        .optional(),
    /**
     * Protected (encrypting) sensitivity labels on artifacts a session produced (Purview phase 4):
     * with `sharepoint` on, a host that cannot apply such a label itself asks the AIOE to apply it
     * through SharePoint (`POST /m365/v1/purview/apply-label`, ./purviewApply.ts). Off, or without
     * the clause, such a label is refused on the machine unless an MIP labeler sidecar is installed.
     */
    protectedLabels: z.object({ sharepoint: z.boolean().default(false) }).optional(),
    /**
     * Whether the organisation sends its security-worthy audit rows to Microsoft Sentinel (the AIOE's
     * connector, filled in when the policy is served): a workbench's Data protection page can then
     * say plainly whether its security events reach Sentinel, rather than "when connected".
     */
    sentinel: z.object({ connected: z.boolean() }).optional(),
});
/** The default the clause amounts to when a policy leaves it out. */
export const DEFAULT_TELEMETRY_CONTENT = "redacted";
/**
 * How serious an audit row is (C2), assigned where the row is written
 * (src/control/auditSeverity.ts):
 *
 *  - `high` — a DLP block, a prompt injection, a tool call refused by policy,
 *    a policy file tampered with, a broken audit chain, an agent quarantined
 *    or disabled by the organisation's registry, a forged signature;
 *  - `warn` — any other denial or error, and a DLP redaction;
 *  - `info` — everything else.
 */
export const auditSeveritySchema = z.enum(["info", "warn", "high"]);
/**
 * `POST /m365/v1/agent365/token` (the AIOE): a token for one agent identity,
 * for the direct path. The device's own AIOE token is the bearer; the AIOE
 * checks the tenant has the direct path on and the identity belongs to it.
 */
export const agent365TokenRequestSchema = z.object({
    /** The specialist (or team role) the run belongs to, by the organisation's id. */
    specialistId: z.string().min(1).max(200).optional(),
    /** The agent identity's id, when the host already knows it (a team member card carries it). */
    entraAgentId: z.string().min(1).max(200).optional(),
    /** The agent's name in its team, for the AIOE to resolve one when neither id is known. */
    agentName: z.string().min(1).max(200).optional(),
    teamId: z.string().min(1).max(200).optional(),
    projectId: z.string().min(1).max(200).optional(),
    rootId: z.string().min(1).max(200).optional(),
});
export const agent365TokenAnswerSchema = z.object({
    /** App-only token for the agent identity, audience the Agent 365 observability service. */
    accessToken: z.string().min(1),
    /** ISO time the token stops working. */
    expiresAt: z.string(),
    /** The customer's Entra tenant id: the `{tenantId}` of the route. */
    tenantId: z.string().min(1).max(100),
    /** The agent identity's appId: the `{agentId}` of the route and `gen_ai.agent.id` on every span. */
    agentAppId: z.string().min(1).max(100),
    /** The blueprint's appId: `microsoft.a365.agent.blueprint.id`. */
    blueprintId: z.string().min(1).max(100),
    /** The Agent 365 service base URL; the AIOE answers it so the host names no Microsoft host of its own. */
    endpoint: z.string().url(),
    /** `observabilityService` (service-to-service, the default) or `observability` (on behalf of a person). */
    route: z.enum(["observabilityService", "observability"]).default("observabilityService"),
    /** The person's Entra object id, when the AIOE knows it: `user.id` on every span. */
    userId: z.string().max(100).optional(),
    /** The AIOE's host name: `server.address`. */
    serverAddress: z.string().max(300).optional(),
});
/** The error codes `POST /m365/v1/agent365/token` answers with (body `{ error }`). */
export const AGENT365_TOKEN_ERRORS = ["not_enabled", "no_identity", "tenant_not_licensed", "consent_required"];
//# sourceMappingURL=microsoft365.js.map